import { BrowserProvider, JsonRpcProvider, Contract, parseUnits, formatUnits } from "ethers";
import { useWeb3ModalProvider, useWeb3ModalAccount } from "@web3modal/ethers/react";

// ----------------------------
// BURNER CONTRACT
// ----------------------------
const BURNER_CONTRACT = "0xC06Cf9ecEA9a73A89BD0Bb92aec6c1A84d72bb43";

const BURNER_ABI = [
  "function burnTokens(address token, uint256 amount) external",
  "function burnedTokensCount() view returns (uint256)",
  "function getAllBurnedTokens() view returns (address[])",
  "function getTotalBurnedPerToken(address token) view returns (uint256)",
  "function getUserBurnsForToken(address user, address token) view returns (tuple(uint256 amount,uint256 fee,uint256 timestamp)[])",
  "function getTotalGlobalBurned() view returns (uint256)"
];

const ERC20_ABI = [
  "function decimals() view returns (uint8)",
  "function balanceOf(address) view returns (uint256)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function approve(address spender, uint256 amount) external returns (bool)"
];

export default function useContract() {
  const { walletProvider } = useWeb3ModalProvider();
  const { isConnected, address } = useWeb3ModalAccount();

  // ----------------------------
  // PROVIDER HELPERS
  // ----------------------------

  const getReadProvider = () => {
    return walletProvider
      ? new BrowserProvider(walletProvider)
      : new JsonRpcProvider("https://bsc-dataseed.bnbchain.org");
  };

  const getSigner = async () => {
    if (!walletProvider) throw new Error("Wallet not connected");
    const provider = new BrowserProvider(walletProvider);
    return await provider.getSigner();
  };

  // ----------------------------
  // 🔥 BURN TOKENS
  // ----------------------------
  const burnTokens = async (tokenAddress, amountHuman) => {
    if (!isConnected) throw new Error("Wallet not connected");

    const signer = await getSigner();

    const erc20 = new Contract(tokenAddress, ERC20_ABI, signer);
    const decimals = Number(await erc20.decimals());
    const amount = parseUnits(amountHuman.toString(), decimals);

    // Check allowance
    const rawAllowance = await erc20.allowance(address, BURNER_CONTRACT);
    const allowance = Number(formatUnits(rawAllowance, decimals));

    if (allowance < Number(amountHuman)) {
      const approveTx = await erc20.approve(BURNER_CONTRACT, amount);
      await approveTx.wait();
    }

    // Burn tokens
    const burner = new Contract(BURNER_CONTRACT, BURNER_ABI, signer);
    const tx = await burner.burnTokens(tokenAddress, amount);
    await tx.wait();

    return tx.hash;
  };

  // ----------------------------
  // 💰 GET USER TOKEN BALANCE
  // ----------------------------
  const getTokenBalance = async (tokenAddress) => {
    if (!isConnected || !address) return 0;

    const provider = getReadProvider();
    const erc20 = new Contract(tokenAddress, ERC20_ABI, provider);

    const [balance, decimals] = await Promise.all([
      erc20.balanceOf(address),
      erc20.decimals()
    ]);

    return Number(formatUnits(balance, decimals));
  };

  // ----------------------------
  // 🌐 GLOBAL STATS
  // ----------------------------
  const getGlobalBurnStats = async () => {
    const provider = getReadProvider();
    const burner = new Contract(BURNER_CONTRACT, BURNER_ABI, provider);

    const burnedTokens = await burner.getAllBurnedTokens();
    const rawTotal = await burner.getTotalGlobalBurned();

    const totalPerToken = {};

    for (let token of burnedTokens) {
      const raw = await burner.getTotalBurnedPerToken(token);
      totalPerToken[token] = Number(raw);
    }

    return {
      burnedTokens,
      totalPerToken,
      totalGlobalBurned: Number(rawTotal),
      burnCount: burnedTokens.length
    };
  };

  // ----------------------------
  // 👤 USER BURN HISTORY
  // ----------------------------
  const getUserBurnHistory = async () => {
    if (!isConnected || !address) return [];

    const provider = getReadProvider();
    const burner = new Contract(BURNER_CONTRACT, BURNER_ABI, provider);

    const burnedTokens = await burner.getAllBurnedTokens();

    const history = [];

    for (let token of burnedTokens) {
      const burns = await burner.getUserBurnsForToken(address, token);

      const erc20 = new Contract(token, ERC20_ABI, provider);
      const decimals = Number(await erc20.decimals());

      const userBurns = burns.map((b) => ({
        token,
        amount: Number(formatUnits(b.amount, decimals)),
        fee: Number(formatUnits(b.fee, decimals)),
        timestamp: Number(b.timestamp)
      }));

      history.push(...userBurns);
    }

    return history;
  };

  return {
    burnTokens,
    getTokenBalance,
    getGlobalBurnStats,
    getUserBurnHistory
  };
}
